import React, { Fragment } from "react";
import { isJust, isNothing, fromMaybe, equals } from "sanctuary";
import { range } from "ramda";
import styled from "styled-components";
import {
  Box,
  Module,
  Center,
  Heading,
  Text,
  Alert,
  CustomerSearchIcon,
  NoResultsIcon,
  Paragraph,
  constants,
} from "@thecb/components";
import { toFormattedDateNoTimeLocalized } from "@/util/dates";

import {
  Table,
  TableHeader,
  TableHeading,
  TableBody,
  TableRow,
  TableRowEntry,
} from "@/components/generic/NewTable";
import { ScrollArea } from "@/components/generic/PageLayout";
import LoadingLine from "@/components/generic/LoadingLine";
import Pagination from "@/components/pagination";
import { WHITE } from "@/constants/colors";
import { CONTENT_MAX_WIDTH } from "@/constants/layout";
import NewTabIcon from "./NewTabIcon";

const TableContainer = styled.div`
  border-radius: 4px;
  width: 100%;
  background-color: ${WHITE};
  margin-bottom: 16px;
  & > div {
    border-radius: 4px;
  }
`;

const RESULTS_PER_PAGE = 20;

const openInNewTab = (url, status, profileId, setCustomerPendingError) => {
  if (status !== "pending") {
    const path = customerManagementPath({ profileId });
    window.open(`${url}${path}`);
  } else {
    setCustomerPendingError();
  }
};

export const customerManagementPath = ({ profileId }) => {
  const profilePath = `admin/${profileId}/profile/settings`;
  return `admin/login/okta?after=/${profilePath}`;
};

const CustomerRow = ({
  id,
  firstName,
  lastName,
  email,
  createdAt,
  status,
  url,
  setCustomerPendingError,
}) => (
  <TableRow
    onClick={() => openInNewTab(url, status, id, setCustomerPendingError)}
    data-qa={id}
  >
    <TableRowEntry data-qa="td-customer-name">{`${firstName} ${lastName}`}</TableRowEntry>
    <TableRowEntry data-qa="td-email">{email}</TableRowEntry>
    <TableRowEntry data-qa="td-customer-since">{createdAt}</TableRowEntry>
    <TableRowEntry data-qa="td-status">
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </TableRowEntry>
    <TableRowEntry data-qa="td-view">
      <Box
        padding="0"
        extraStyles="white-space: nowrap; display: flex; justify-content: flex-end;"
      >
        <Text
          color={constants.colors.LUCKY_POINT}
          hoverStyles="cursor: pointer;"
          extraStyles="padding-right: 8px;"
        >
          View
        </Text>
        <NewTabIcon />
      </Box>
    </TableRowEntry>
  </TableRow>
);

const FakeRow = (
  <TableRow>
    <TableRowEntry>
      <LoadingLine exactWidth={140} />
    </TableRowEntry>
    <TableRowEntry>
      <LoadingLine exactWidth={180} />
    </TableRowEntry>
    <TableRowEntry>
      <LoadingLine exactWidth={90} />
    </TableRowEntry>
    <TableRowEntry>
      <LoadingLine exactWidth={60} />
    </TableRowEntry>
    <TableRowEntry extraStyles="display: flex; justify-content: flex-end;">
      <LoadingLine exactWidth={90} />
    </TableRowEntry>
  </TableRow>
);

const FakeRowSecondary = (
  <TableRow>
    <TableRowEntry>
      <LoadingLine exactWidth={160} />
    </TableRowEntry>
    <TableRowEntry>
      <LoadingLine exactWidth={190} />
    </TableRowEntry>
    <TableRowEntry>
      <LoadingLine exactWidth={90} />
    </TableRowEntry>
    <TableRowEntry>
      <LoadingLine exactWidth={90} />
    </TableRowEntry>
    <TableRowEntry extraStyles="display: flex; justify-content: flex-end;">
      <LoadingLine exactWidth={90} />
    </TableRowEntry>
  </TableRow>
);

const CustomersTable = ({
  customers,
  userTimezone,
  waitingForSearch,
  searchRequested,
  searchFailed,
  searchResultsPageCount = 0,
  pagePrevious,
  pageNext,
  setCurrentPage,
  currentPage,
  showCustomerPendingError,
  setCustomerPendingError,
  removeCustomerPendingError,
  navigateURL,
}) => {
  const noResults =
    isJust(customers) &&
    !waitingForSearch &&
    isNothing(searchFailed) &&
    equals([])(customers.value) &&
    searchRequested;
  const somethingWentWrong = isJust(searchFailed);

  return (
    <Fragment>
      {showCustomerPendingError && (
        <Box
          padding="0"
          width="90%"
          maxWidth={CONTENT_MAX_WIDTH}
          extraStyles="position: fixed; top: 64px; left: 50%; transform: translateX(-50%); z-index: 1000;"
        >
          <Alert
            variant="error"
            text="You can't view this customer until they complete setting up their profile."
            height="56px"
            onLinkClick={removeCustomerPendingError}
          />
        </Box>
      )}
      <Box padding="32px 0 0 0" width="100%">
        <Module spacingBottom="0">
          <TableContainer>
            <ScrollArea>
              <Table>
                <TableHeader>
                  <TableHeading>Customer name</TableHeading>
                  <TableHeading>Email address</TableHeading>
                  <TableHeading>Customer since</TableHeading>
                  <TableHeading>Status</TableHeading>
                  <TableHeading>Actions</TableHeading>
                </TableHeader>
                <TableBody>
                  {waitingForSearch
                    ? range(0, RESULTS_PER_PAGE / 2).map((idx) => (
                        <Fragment key={idx}>
                          {FakeRow}
                          {FakeRowSecondary}
                        </Fragment>
                      ))
                    : fromMaybe([])(customers).map(
                        ({
                          id,
                          firstName,
                          lastName,
                          email,
                          createdAt,
                          type,
                          status,
                          title,
                        }) => (
                          <CustomerRow
                            key={fromMaybe("")(id)}
                            id={fromMaybe("")(id)}
                            firstName={fromMaybe("")(firstName)}
                            lastName={fromMaybe("")(lastName)}
                            email={fromMaybe("")(email)}
                            createdAt={toFormattedDateNoTimeLocalized(
                              createdAt,
                              userTimezone
                            )}
                            type={fromMaybe("")(type)}
                            status={fromMaybe("")(status)}
                            title={fromMaybe("")(title)}
                            url={navigateURL}
                            setCustomerPendingError={setCustomerPendingError}
                          />
                        )
                      )}
                </TableBody>
              </Table>
            </ScrollArea>
            {!searchRequested && (
              <Box padding="64px" minWidth="100%">
                <Center intrinsic>
                  <CustomerSearchIcon
                    fillPrimary={constants.colors.LUCKY_POINT}
                    fillSecondary={constants.colors.HAWKES_BLUE}
                  />
                  <Heading variant="h3" weight="700">
                    Perform a Search
                  </Heading>
                  <Paragraph variant="p" extraStyles={`text-align: center;`}>
                    Please perform a search in the search <br /> bar above to see
                    customers.
                  </Paragraph>
                </Center>
              </Box>
            )}
            {noResults && (
              <Box padding="64px" minWidth="100%">
                <Center intrinsic>
                  <NoResultsIcon
                    fillPrimary={constants.colors.LUCKY_POINT}
                    fillSecondary={constants.colors.HAWKES_BLUE}
                  />
                  <Heading variant="h3" weight="700">
                    No Results Found
                  </Heading>
                  <Paragraph variant="p" extraStyles={`text-align: center;`}>
                    We couldn’t find anything that match your search. <br />
                    Please edit your search again and try again.
                  </Paragraph>
                </Center>
              </Box>
            )}
            {somethingWentWrong && (
              <Box padding="64px" minWidth="100%">
                <Center intrinsic>
                  <NoResultsIcon
                    fillPrimary={constants.colors.LUCKY_POINT}
                    fillSecondary={constants.colors.HAWKES_BLUE}
                  />
                  <Heading variant="h3" weight="700">
                    Something Went Wrong
                  </Heading>
                  <Paragraph variant="p" extraStyles={`text-align: center;`}>
                    Something went wrong, please try your search again.
                    <br />
                    If the problem persists, please contact your administrator.
                  </Paragraph>
                </Center>
              </Box>
            )}
          </TableContainer>
        </Module>
      </Box>
      {searchResultsPageCount > 1 && (
        <Box padding="0" minWidth="100%">
          <Module padding="24px" spacingBottom="0">
            <Pagination
              pagePrevious={pagePrevious}
              pageNext={pageNext}
              setCurrentPage={setCurrentPage}
              currentPage={currentPage}
              pageCount={searchResultsPageCount}
            />
          </Module>
        </Box>
      )}
    </Fragment>
  );
};

export default CustomersTable;
